import {
  canonicalLevelColorScheme,
  jumpCourseSuperfix,
  type JumpCourseSuperfix,
} from '@wht-xc-utils/shared/domain';
import type { PacketPdfPage } from '../components/PacketPdfDocument';

const TILE_SIZE = 256;
const DEFAULT_MAP_WIDTH = 370;
const DEFAULT_MAP_HEIGHT = 156;
const MAX_ZOOM = 16;
const PIXEL_RATIO = 2;
const tileCache = new Map<string, Promise<ImageBitmap>>();
const mapCache = new Map<string, Promise<string | null>>();

export interface StaticPostMapPoint {
  latitude: number;
  longitude: number;
  label: string;
  superfix?: JumpCourseSuperfix | null;
  backgroundColor: string;
  textColor: string;
}

interface ProjectedMarker {
  x: number;
  y: number;
  label: string;
  superfix?: JumpCourseSuperfix | null;
  backgroundColor: string;
  textColor: string;
  width: number;
}

export interface MarkerLayout {
  x: number;
  y: number;
  segments: Array<{
    label: string;
    superfix?: JumpCourseSuperfix | null;
    backgroundColor: string;
    textColor: string;
    width: number;
  }>;
}

const MARKER_HEIGHT = 15;
const MARKER_BORDER_WIDTH = 1.5;

export async function addPacketMapImages(pages: PacketPdfPage[]): Promise<PacketPdfPage[]> {
  const result = new Array<PacketPdfPage>(pages.length);
  let nextIndex = 0;

  async function renderNext(): Promise<void> {
    while (nextIndex < pages.length) {
      const index = nextIndex++;
      const page = pages[index];
      if (!page) continue;
      result[index] = {
        ...page,
        mapImage: await renderPacketMap(page),
      };
    }
  }

  await Promise.all(Array.from({ length: Math.min(4, pages.length) }, () => renderNext()));
  return result;
}

function renderPacketMap(page: PacketPdfPage): Promise<string | null> {
  const labelCandidates = page.rows.flatMap((row) =>
    row.jumps.map((jump) => ({
      canonicalSlug: row.canonicalSlug,
      number: jump.number,
      name: jump.name,
      latitude: jump.latitude,
      longitude: jump.longitude,
    })),
  );
  const points = page.rows.flatMap((row) => {
    const colors = canonicalLevelColorScheme(row.canonicalSlug);
    return row.jumps.flatMap((jump): StaticPostMapPoint[] =>
      jump.latitude == null || jump.longitude == null
        ? []
        : [
            {
              latitude: jump.latitude,
              longitude: jump.longitude,
              label: jump.number,
              superfix: jumpCourseSuperfix(
                {
                  canonicalSlug: row.canonicalSlug,
                  number: jump.number,
                  name: jump.name,
                  latitude: jump.latitude,
                  longitude: jump.longitude,
                },
                labelCandidates,
              ),
              backgroundColor: colors.backgroundColor,
              textColor: colors.textColor,
            },
          ],
    );
  });
  if (points.length === 0) return Promise.resolve(null);

  return renderPostMapImage(points);
}

export function renderPostMapImage(
  points: StaticPostMapPoint[],
  size = { width: DEFAULT_MAP_WIDTH, height: DEFAULT_MAP_HEIGHT },
): Promise<string | null> {
  if (points.length === 0) return Promise.resolve(null);
  const cacheKey = JSON.stringify({ points, size });
  const cached = mapCache.get(cacheKey);
  if (cached) return cached;

  const rendered = composeMap(points, size);
  mapCache.set(cacheKey, rendered);
  return rendered;
}

async function composeMap(
  points: StaticPostMapPoint[],
  size: { width: number; height: number },
): Promise<string> {
  const mapWidth = Math.max(1, Math.round(size.width));
  const mapHeight = Math.max(1, Math.round(size.height));
  const canvas = document.createElement('canvas');
  canvas.width = mapWidth * PIXEL_RATIO;
  canvas.height = mapHeight * PIXEL_RATIO;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create a canvas for packet maps');
  context.scale(PIXEL_RATIO, PIXEL_RATIO);

  const zoom = chooseZoom(points, mapWidth, mapHeight);
  const projected = points.map((point) => project(point.latitude, point.longitude, zoom));
  const bounds = pixelBounds(projected);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const left = centerX - mapWidth / 2;
  const top = centerY - mapHeight / 2;
  const minTileX = Math.floor(left / TILE_SIZE);
  const maxTileX = Math.floor((left + mapWidth) / TILE_SIZE);
  const minTileY = Math.floor(top / TILE_SIZE);
  const maxTileY = Math.floor((top + mapHeight) / TILE_SIZE);
  const tileCount = 2 ** zoom;

  const tileRequests: Array<Promise<{ image: ImageBitmap; x: number; y: number }>> = [];
  for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
    if (tileY < 0 || tileY >= tileCount) continue;
    for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
      const wrappedX = ((tileX % tileCount) + tileCount) % tileCount;
      tileRequests.push(
        loadTile(zoom, wrappedX, tileY).then((image) => ({
          image,
          x: tileX * TILE_SIZE - left,
          y: tileY * TILE_SIZE - top,
        })),
      );
    }
  }
  const tiles = await Promise.all(tileRequests);

  for (const tile of tiles) {
    context.drawImage(tile.image, tile.x, tile.y, TILE_SIZE, TILE_SIZE);
  }

  if (projected.length > 1) {
    context.beginPath();
    projected.forEach((point, index) => {
      const x = point.x - left;
      const y = point.y - top;
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    });
    context.strokeStyle = '#a95732';
    context.lineWidth = 2.5;
    context.setLineDash([6, 5]);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.stroke();
    context.setLineDash([]);
  }

  context.font = 'bold 8px sans-serif';
  const markers = layoutMapMarkers(
    points.flatMap((point, index): ProjectedMarker[] => {
      const pixel = projected[index];
      return pixel
        ? [
            {
              x: pixel.x - left,
              y: pixel.y - top,
              label: point.label,
              superfix: point.superfix,
              backgroundColor: point.backgroundColor,
              textColor: point.textColor,
              width: Math.max(18, markerTextWidth(context, point.label, point.superfix) + 8),
            },
          ]
        : [];
    }),
  );
  for (const marker of markers) {
    drawMarker(context, marker);
  }
  drawAttribution(context, mapWidth, mapHeight);

  return canvas.toDataURL('image/png');
}

function chooseZoom(points: StaticPostMapPoint[], mapWidth: number, mapHeight: number): number {
  for (let zoom = MAX_ZOOM; zoom >= 11; zoom -= 1) {
    const bounds = pixelBounds(
      points.map((point) => project(point.latitude, point.longitude, zoom)),
    );
    if (bounds.maxX - bounds.minX <= mapWidth - 70 && bounds.maxY - bounds.minY <= mapHeight - 44) {
      return zoom;
    }
  }
  return 11;
}

function project(latitude: number, longitude: number, zoom: number): { x: number; y: number } {
  const scale = TILE_SIZE * 2 ** zoom;
  const clampedLatitude = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  const sinLatitude = Math.sin((clampedLatitude * Math.PI) / 180);
  return {
    x: ((longitude + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) * scale,
  };
}

function pixelBounds(points: Array<{ x: number; y: number }>): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} {
  return points.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point.x),
      maxX: Math.max(bounds.maxX, point.x),
      minY: Math.min(bounds.minY, point.y),
      maxY: Math.max(bounds.maxY, point.y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  );
}

function loadTile(zoom: number, x: number, y: number): Promise<ImageBitmap> {
  const url =
    `https://server.arcgisonline.com/ArcGIS/rest/services/` +
    `World_Imagery/MapServer/tile/${zoom}/${y}/${x}`;
  const cached = tileCache.get(url);
  if (cached) return cached;

  const request = fetch(url).then(async (response) => {
    if (!response.ok) {
      throw new Error(`Could not load packet map tile (${response.status})`);
    }
    return createImageBitmap(await response.blob());
  });
  tileCache.set(url, request);
  return request;
}

export function layoutMapMarkers(points: ProjectedMarker[]): MarkerLayout[] {
  const groups = points.map((point) => ({ points: [point] }));

  for (let leftIndex = 0; leftIndex < groups.length; leftIndex += 1) {
    let rightIndex = leftIndex + 1;
    while (rightIndex < groups.length) {
      const leftGroup = groups[leftIndex];
      const rightGroup = groups[rightIndex];
      if (!leftGroup || !rightGroup) {
        rightIndex += 1;
        continue;
      }
      if (
        !markerBoundsOverlap(
          markerGroupLayout(leftGroup.points),
          markerGroupLayout(rightGroup.points),
        )
      ) {
        rightIndex += 1;
        continue;
      }
      leftGroup.points.push(...rightGroup.points);
      groups.splice(rightIndex, 1);
      leftIndex = -1;
      break;
    }
  }

  return groups.map((group) => markerGroupLayout(group.points));
}

function markerGroupLayout(points: ProjectedMarker[]): MarkerLayout {
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
    segments: points.map(({ label, superfix, backgroundColor, textColor, width }) => ({
      label,
      superfix,
      backgroundColor,
      textColor,
      width,
    })),
  };
}

function markerBoundsOverlap(left: MarkerLayout, right: MarkerLayout): boolean {
  const leftWidth = left.segments.reduce((sum, segment) => sum + segment.width, 0);
  const rightWidth = right.segments.reduce((sum, segment) => sum + segment.width, 0);
  const horizontalDistance = Math.abs(left.x - right.x);
  const verticalDistance = Math.abs(left.y - right.y);
  return (
    horizontalDistance < (leftWidth + rightWidth) / 2 + MARKER_BORDER_WIDTH * 2 &&
    verticalDistance < MARKER_HEIGHT + MARKER_BORDER_WIDTH * 2
  );
}

function drawMarker(context: CanvasRenderingContext2D, marker: MarkerLayout): void {
  context.font = 'bold 8px sans-serif';
  const width = marker.segments.reduce((sum, segment) => sum + segment.width, 0);
  const left = marker.x - width / 2;
  const top = marker.y - MARKER_HEIGHT / 2;

  context.fillStyle = '#ffffff';
  drawRoundedRect(
    context,
    left - MARKER_BORDER_WIDTH,
    top - MARKER_BORDER_WIDTH,
    width + MARKER_BORDER_WIDTH * 2,
    MARKER_HEIGHT + MARKER_BORDER_WIDTH * 2,
    5,
  );
  context.fill();

  context.save();
  drawRoundedRect(context, left, top, width, MARKER_HEIGHT, 4);
  context.clip();
  let segmentLeft = left;
  marker.segments.forEach((segment, index) => {
    context.fillStyle = segment.backgroundColor;
    context.fillRect(segmentLeft, top, segment.width, MARKER_HEIGHT);
    if (index > 0) {
      context.fillStyle = '#ffffff';
      context.fillRect(segmentLeft - 0.5, top, 1, MARKER_HEIGHT);
    }
    context.fillStyle = segment.textColor;
    drawMarkerLabel(context, segment, segmentLeft, marker.y);
    segmentLeft += segment.width;
  });
  context.restore();
}

function markerTextWidth(
  context: CanvasRenderingContext2D,
  label: string,
  superfix: JumpCourseSuperfix | null | undefined,
): number {
  context.font = 'bold 8px sans-serif';
  const labelWidth = context.measureText(label).width;
  if (!superfix) return labelWidth;
  context.font = 'bold 5px sans-serif';
  return labelWidth + context.measureText(superfix).width + 1;
}

function drawMarkerLabel(
  context: CanvasRenderingContext2D,
  segment: MarkerLayout['segments'][number],
  segmentLeft: number,
  centerY: number,
): void {
  const totalWidth = markerTextWidth(context, segment.label, segment.superfix);
  let textLeft = segmentLeft + (segment.width - totalWidth) / 2;
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  context.font = 'bold 8px sans-serif';
  context.fillText(segment.label, textLeft, centerY + 0.5);
  textLeft += context.measureText(segment.label).width + 1;
  if (segment.superfix) {
    context.font = 'bold 5px sans-serif';
    context.textBaseline = 'alphabetic';
    context.fillText(segment.superfix, textLeft, centerY - 1);
  }
}

function drawRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

function drawAttribution(
  context: CanvasRenderingContext2D,
  mapWidth: number,
  mapHeight: number,
): void {
  const label = 'Imagery © Esri';
  context.font = '6px sans-serif';
  const width = context.measureText(label).width + 5;
  context.fillStyle = 'rgba(255, 255, 255, 0.78)';
  context.fillRect(mapWidth - width, mapHeight - 9, width, 9);
  context.fillStyle = '#333333';
  context.textAlign = 'right';
  context.textBaseline = 'alphabetic';
  context.fillText(label, mapWidth - 2, mapHeight - 2);
}
